/**
 * Google AdSense ad unit component. Renders a single ad slot and triggers fill on mount.
 * Use slotKey from adConfig (BANNER, SIDEBAR, IN_FEED, RECTANGLE) or pass slotId directly.
 * If no slot is configured, renders nothing so dev/local doesn't show broken units.
 */

import React, { useEffect, useRef } from 'react';
import { getAdClient, getAdSlot } from '../config/adConfig';
import { usePlan } from '../contexts/PlanContext';
import './GoogleAd.css';

const AD_FORMAT = {
  AUTO: 'auto',
  RECTANGLE: 'rectangle',
  HORIZONTAL: 'horizontal',
  VERTICAL: 'vertical',
};

export default function GoogleAd({
  slotKey = 'BANNER',
  slotId: slotIdProp,
  format = AD_FORMAT.AUTO,
  className = '',
  style = {},
  label = 'Ad',
}) {
  const containerRef = useRef(null);
  const { showAds } = usePlan();
  const slotId = slotIdProp || getAdSlot(slotKey);
  const client = getAdClient();

  const shouldRender = showAds !== false && (slotIdProp || getAdSlot(slotKey)) && client;

  useEffect(() => {
    if (!shouldRender || !containerRef.current || !slotId) return;
    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
    } catch (e) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[GoogleAd] push failed:', e?.message);
      }
    }
  }, [shouldRender, slotId]);

  if (!shouldRender) {
    return null;
  }

  return (
    <div
      ref={containerRef}
      className={`google-ad-wrap ${className}`.trim()}
      style={style}
    >
      {label && (
        <span className="google-ad-label" aria-hidden="true">
          {label}
        </span>
      )}
      <ins
        className="adsbygoogle"
        style={{ display: 'block' }}
        data-ad-client={client}
        data-ad-slot={slotId}
        data-ad-format={format}
        data-full-width-responsive={format === AD_FORMAT.AUTO ? 'true' : undefined}
      />
    </div>
  );
}

export { AD_FORMAT };
